<template>
  <v-navigation-drawer v-model="menuNavigation" class="menu-navigation wd-w-full !wd-h-full !wd-top-0" width="0" temporary location="right" touchless>
    <v-toolbar flat color="white" class="wd-max-h-14">
      <v-toolbar-title class="wd-mx-auto" />

      <v-btn class="close-btn mr-5" min-width="24" width="24" height="24" icon @click="menuNavigation = false">
        <funnow-icon max-width="24" height="24" image-path="cancel-icon_black.svg" />
      </v-btn>
    </v-toolbar>

    <v-list v-if="isLanguageSettingOnly" v-model="language" color="orange50" mandatory class="wd-py-0" :min-width="185">
      <v-list-item
        v-for="locale in languageList"
        :key="locale.code"
        class="wd-px-5"
        :value="locale.code"
        :active="locale.active"
        @click="changeLanguage(locale)"
      >
        <v-list-item-title> {{ locale.text }} </v-list-item-title>
      </v-list-item>
    </v-list>

    <template v-else>
      <!-- User Row -->
      <div class="user-row wd-flex wd-items-center wd-px-5 wd-py-[10px] wd-bg-white">
        <funnow-icon v-if="$auth.user" max-width="40" height="40" image-path="member-icon.svg" class="mr-3" />
        <funnow-icon v-else max-width="56" height="56" image-path="guest-avatar.svg" class="mr-3" />
        <span class="user-name">{{ $auth.user ? $auth.user.name : $t('guest') }}</span>
      </div>
      <v-divider :color="CUSTOM_COLORS.gray40" />

      <MenuLanguageAndDownload @language-changed="menuNavigation = false" />
      <v-divider :color="CUSTOM_COLORS.gray40" />

      <!-- Login/Register Row (guest only) -->
      <div v-if="!$auth.user" class="login-row wd-flex wd-items-center wd-px-5 wd-py-3 wd-bg-white wd-cursor-pointer" @click="openLoginDialog">
        <span class="row-text wd-flex-1">{{ $t('header_register_login') }}</span>
        <funnow-icon max-width="20" height="20" image-path="chevron-right.svg" />
      </div>
      <v-divider v-if="!$auth.user" :color="CUSTOM_COLORS.gray40" />
    </template>
  </v-navigation-drawer>
</template>

<script>
import { unref } from 'vue'
import { mapGetters } from 'vuex'
import { mapWritableState } from 'pinia'

import { usePlatformStore } from '@/store-pinia/platform'
import { LoginDialogTrigger } from '@/view-models/account'
import { CUSTOM_COLORS } from '@/constants/theme'

import FunnowIcon from '@/components/icons/funnow-icon'
import MenuLanguageAndDownload from '@/components/navigation/MenuLanguageAndDownload.vue'

export default defineComponent({
  name: 'HeaderMenu',
  components: {
    FunnowIcon,
    MenuLanguageAndDownload,
  },
  setup() {
    const { loadLocaleMessages } = useLocaleMessages()

    const { isFunBookV2 } = storeToRefs(usePlatformStore())
    const isLanguageSettingOnly = computed(() => isFunBookV2.value)

    return {
      isLanguageSettingOnly,
      loadLocaleMessages,
      CUSTOM_COLORS,
    }
  },
  data() {
    return {
      defaultCurrency: 0,
      language: this.$i18n.locale,
    }
  },
  computed: {
    ...mapGetters({
      user: 'authentication/getUser',
      regions: 'region/allRegions',
      currencies: 'currency/allCurrency',
    }),
    ...mapWritableState(usePlatformStore, ['isShowLoginStep']),
    menuNavigation: {
      get() {
        return this.$store.state.menuNavigation
      },
      set(val) {
        this.setMenuNavigation(val)
      },
    },
    selectedLanguageText() {
      const locales = unref(this.$i18n.locales)
      const locale = unref(this.$i18n.locale)
      const currLocale = locales.find(l => l.code === locale)
      return currLocale?.text || ''
    },
    languageList() {
      const locales = unref(this.$i18n.locales)
      const locale = unref(this.$i18n.locale)
      return locales?.map(l => ({ ...l, active: l.code === locale })) || []
    },
    currencyList() {
      return this.currencies.map(currency => {
        return { value: currency.id, text: currency.code }
      })
    },
  },
  methods: {
    setMenuNavigation(val) {
      this.$store.dispatch('setMenuNavigation', val)
    },
    changeCurrency(currency) {
      this.setMenuNavigation(false)
    },
    async changeLanguage({ code }) {
      await this.loadLocaleMessages(code)
      this.$vuetify.locale.current = code
      await this.$i18n.setLocale(code)
      this.$store.dispatch('region/getAllRegion')
      this.menuNavigation = false
    },
    openLoginDialog() {
      this.isShowLoginStep = true
      this.$store.dispatch('dialog/setLoginDialog', { state: true, options: { from: LoginDialogTrigger.Header } })
      this.menuNavigation = false
    },
  },
})
</script>

<style lang="scss" scoped>
.menu-navigation {
  z-index: 20;

  .close-btn {
    position: absolute;
    right: 0;
  }

  .user-row {
    .user-name {
      font-size: 18px;
      font-weight: 500;
      color: #252729;
      line-height: 1.32;
    }
  }

  .login-row {
    .row-text {
      font-size: 16px;
      font-weight: 400;
      color: #252729;
      line-height: 1.48;
    }
  }
}
</style>
